import { useEffect, useRef } from 'react';
import { Button } from '../ui';
import { useQueueFlow } from './flowContext';
import { useQueueCardAsk } from './useQueueCardAsk';
import { useCopy } from '../strings/useCopy';
import type { QueueCandidate } from './model';
import styles from './queue.module.css';

/**
 * What a press on a video means while a queue is running: the card answers on its own picture.
 *
 * Two acts rather than one, which is why the press is claimed at all — opening a video while a list
 * is running either ends the list or extends it, and that difference is a list somebody spent time
 * building. **Play now** clears the queue and opens the video; **Play next** puts it directly after
 * the one playing and leaves the reader where they are. Half the picture each, so neither is the one
 * a hurried press lands on.
 *
 * On the card rather than in a modal (2026-08-08). A question about *this* card belongs on it: the
 * reader is already looking at the picture they pressed, so a box in the middle of the screen has to
 * name the video back to them to say what it is about, and a modal takes the whole page's focus for
 * a choice between two presses. Play now is unconfirmed here for the same reason the card's own
 * Remove is: two buttons on a picture are a pair of targets, not a trap, the reader is choosing
 * between them, and the same press that opened them is called off by pressing anywhere else.
 *
 * A **sibling** of the thumbnail's `<a>` and never a child, `listing/CardActions.tsx`'s arrangement
 * and its reason: a `<button>` inside an `<a>` is invalid HTML that browsers resolve by dropping one
 * of the two. Written after the card's text as well, so a keyboard reaches the video's title before
 * the buttons that act on it.
 *
 * The buttons are **buttons, not links**, including the one that navigates: the navigation is a
 * consequence of the act rather than the act itself — the queue is edited first — and an anchor
 * would offer an "open in new tab" that edits this tab's queue and opens the video elsewhere.
 *
 * **Play next is disabled while the list is a playlist**, and stays drawn: this API has no reorder
 * endpoint, so putting a video next is not an act a playlist has (`queueActs`), and its `title` says so.
 * A strip that dropped one of its two answers would read as a different control in the corner of a card
 * the reader has already pressed. Play now is unaffected — it clears the queue, which lets go of the
 * playlist with it, and opens the card's own href.
 */
export function QueueCardAsk({
  item,
  tabIndex,
}: {
  /** The card's own row. `null` for a row with no video behind it, which never asks. */
  readonly item: QueueCandidate | null;
  /** The grid's roving `tabIndex`, the same one the corner controls take (`useGridFocus`). */
  readonly tabIndex?: number;
}) {
  const words = useCopy().text.queue.press;
  const flow = useQueueFlow();
  const asking = useQueueCardAsk(item);
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const dismiss = flow?.dismiss;

  /*
   * The two ways out that are not an answer: a press somewhere else, and Escape. Both put the card
   * back exactly as it was, since nothing about the queue has changed yet.
   *
   * **Capture phase**, which is what `web-ui.md` requires of any document-level "did this land
   * outside me" listener and what also makes the opening press safe: capture runs from the document
   * down, so it has already passed the document by the time the card's own handler adds this — and a
   * bubble-phase listener would catch that very press and close what it just opened.
   *
   * The card is this box's `parentElement`, which is the same element the geometry is measured
   * against (`queue.module.css`): the overlay is a direct child of the card in all three card types.
   */
  useEffect(() => {
    if (!asking || dismiss === undefined) return;
    const card = boxRef.current?.parentElement ?? null;
    const inside = (target: EventTarget | null): boolean =>
      card !== null && target instanceof Node && card.contains(target);

    const onPress = (event: PointerEvent): void => {
      if (!inside(event.target)) dismiss();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      /*
       * Focus goes back to the card, and the card's own link is where it goes: a reader who
       * answered with the keyboard is standing on one of these buttons, and dismissing without
       * saying where focus lands leaves them on `<body>` at the top of the page. The thumbnail's
       * copy of the link is `aria-hidden`, so the title's is the one to name.
       */
      const link = card?.querySelector<HTMLElement>('a[href]:not([aria-hidden="true"])') ?? null;
      dismiss();
      link?.focus();
    };

    document.addEventListener('pointerdown', onPress, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPress, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [asking, dismiss]);

  if (!asking || flow === null || item === null) return null;
  const canPlayNext = flow.acts.playNext;
  return (
    <span className={styles.ask} ref={boxRef}>
      <Button
        variant="media"
        className={styles.askChoice}
        label={words.nowLabel(item.title)}
        title={words.nowTitle}
        tabIndex={tabIndex}
        onClick={flow.playNow}
      >
        <PlayNowGlyph />
        <span className={styles.askWord}>{words.now}</span>
      </Button>
      <Button
        variant="media"
        className={styles.askChoice}
        label={canPlayNext ? words.nextLabel(item.title) : words.nextUnavailableLabel(item.title)}
        /* The reason rides the `title` and the accessible name both: a `disabled` button is a control a
           reader can see and cannot use, so the one thing it owes them is why. */
        title={canPlayNext ? words.nextTitle : words.nextUnavailable}
        tabIndex={tabIndex}
        disabled={!canPlayNext}
        onClick={flow.playNext}
      >
        <PlayNextGlyph />
        <span className={styles.askWord}>{words.next}</span>
      </Button>
    </span>
  );
}

/** Play now: the app's play triangle, on its own. This answer is about the picture, not the list. */
function PlayNowGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M7 4.5 19 12 7 19.5z" />
    </svg>
  );
}

/**
 * Play next: a list of three rows with the **second** one marked by a play triangle. The row under
 * the one playing is where this video lands, so the picture is the position rather than the act.
 */
function PlayNextGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M4 5h16M11.5 12H20M4 19h16" />
      </g>
      <path fill="currentColor" d="M4 8.5 9.5 12 4 15.5z" />
    </svg>
  );
}
